/**
 * Date-range control (PRD "Date Range control", §5.2).
 *
 * 30d (default) / 60d / 90d / 6 months / 12 months / All time / Custom. The range filters by post
 * published date and bounds every Metrics chart; the resolved bounds are shown so the user can see
 * exactly what the server was asked for.
 */

import { RANGE_PRESETS, type RangePreset, type ViewRangeState } from '@lw/shared';
import { cx } from '../../components';
import { formatDateCompact, fromDateInputValue, toDateInputValue } from '../../lib/datetime';
import { RANGE_PRESET_LABEL, resolveRange, type ResolvedRange } from './viewState';

export type RangePanelProps = {
  range: ViewRangeState;
  resolved: ResolvedRange;
  onChange: (next: ViewRangeState) => void;
  timezone: string;
  now: number;
};

export function RangePanel({ range, resolved, onChange, timezone, now }: RangePanelProps) {
  const selectPreset = (preset: RangePreset) => {
    if (preset !== 'custom') {
      onChange({ preset });
      return;
    }
    // Seed Custom with whatever the current preset resolved to, so the inputs are never blank.
    const seed = resolveRange(range, now);
    onChange({ preset: 'custom', from: seed.from, to: seed.to });
  };

  return (
    <div className="range-panel">
      <ul className="range-panel__presets">
        {RANGE_PRESETS.map((preset) => (
          <li key={preset}>
            <button
              type="button"
              className={cx('menu__item', range.preset === preset && 'is-active')}
              aria-pressed={range.preset === preset}
              onClick={() => selectPreset(preset)}
            >
              {RANGE_PRESET_LABEL[preset]}
            </button>
          </li>
        ))}
      </ul>

      {range.preset === 'custom' ? (
        <div className="range-panel__custom">
          <div className="field">
            <label className="field__label" htmlFor="view-range-from">
              From
            </label>
            <input
              id="view-range-from"
              className="input"
              type="date"
              value={toDateInputValue(range.from ?? resolved.from)}
              onChange={(event) => {
                const from = fromDateInputValue(event.target.value, 'start');
                if (from !== null) onChange({ ...range, preset: 'custom', from });
              }}
            />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="view-range-to">
              To
            </label>
            <input
              id="view-range-to"
              className="input"
              type="date"
              value={toDateInputValue(range.to ?? resolved.to)}
              onChange={(event) => {
                const to = fromDateInputValue(event.target.value, 'end');
                if (to !== null) onChange({ ...range, preset: 'custom', to });
              }}
            />
          </div>
        </div>
      ) : null}

      <p className="faint range-panel__resolved">
        {range.preset === 'all'
          ? 'Everything up to '
          : `${formatDateCompact(resolved.from, timezone)} → `}
        {formatDateCompact(resolved.to, timezone)}
      </p>
    </div>
  );
}

export default RangePanel;
